"use client";

import { useEffect } from "react";
import { ChevronRight, X } from "lucide-react";

interface ImageLightboxProps {
  src: string;
  alt: string;
  title: string;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
}

export function ImageLightbox({
  src,
  alt,
  title,
  onClose,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
}: ImageLightboxProps) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft" && hasPrev) onPrev();
      if (event.key === "ArrowRight" && hasNext) onNext();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
      >
        <X size={20} />
      </button>

      {hasPrev && (
        <button
          onClick={(event) => {
            event.stopPropagation();
            onPrev();
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center text-white transition-colors z-10"
        >
          <ChevronRight size={22} className="rotate-180" />
        </button>
      )}
      {hasNext && (
        <button
          onClick={(event) => {
            event.stopPropagation();
            onNext();
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center text-white transition-colors z-10"
        >
          <ChevronRight size={22} />
        </button>
      )}

      <div
        className="max-w-5xl w-full rounded-2xl overflow-hidden shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <img src={src} alt={alt} className="w-full max-h-[80vh] object-contain bg-black" />
        <div className="bg-gray-900 px-6 py-3">
          <p className="text-white font-semibold text-sm">{title}</p>
          <p className="text-gray-400 text-xs mt-0.5">{alt}</p>
        </div>
      </div>
    </div>
  );
}
